import React from 'react';

/**
 * ResponsiveImage Component
 * 
 * Serves WebP images with automatic fallback to JPG/PNG for browsers 
 * that don't support WebP. Uses the HTML <picture> element for native
 * browser format selection.
 * 
 * Usage:
 * <ResponsiveImage 
 *   src="/images/services/stained-glass/hero.jpg"
 *   alt="Stained glass restoration"
 *   className="my-image-class"
 *   loading="lazy"
 * />
 */
const ResponsiveImage = ({ 
    src, 
    alt, 
    className = '', 
    loading = 'lazy',
    width = '1600',
    height = '900',
    style,
    ...props 
}) => {
    // Extract the base path and convert to WebP path
    const getWebPPath = (imagePath) => {
        // Handle .jpg, .jpeg, and .png extensions
        const extensionMatch = imagePath.match(/\.(jpg|jpeg|png)$/i);
        if (extensionMatch) {
            return imagePath.replace(/\.(jpg|jpeg|png)$/i, '.webp');
        }
        return imagePath;
    };

    // Get the MIME type for the fallback image
    const getFallbackType = (imagePath) => {
        if (imagePath.match(/\.png$/i)) return 'image/png';
        return 'image/jpeg';
    };

    const webpSrc = getWebPPath(src);
    const fallbackType = getFallbackType(src);

    return (
        <picture>
            {/* WebP source - modern browsers will use this */}
            <source srcSet={webpSrc} type="image/webp" />
            
            {/* Fallback for browsers that don't support WebP */}
            <source srcSet={src} type={fallbackType} />
            
            {/* Fallback img element */}
            <img
                src={src}
                alt={alt}
                className={className}
                loading={loading}
                width={width}
                height={height}
                style={style}
                {...props}
            />
        </picture>
    );
};

export default ResponsiveImage;
